import CircleIcon from '@mui/icons-material/Circle';
import {
  Grid,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

TrackingFileStepper.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      Step: PropTypes.string,
      ActualStatus: PropTypes.string || null,
      ReviewDate: PropTypes.string,
      UserName: PropTypes.string,
    })
  ).isRequired,
};

function TrackingFileStepper({ data, site }) {
  const { t } = useTranslation();
  let cleanedData = data;
  if (data && data.length > 0) {
    const coordReviewBeforeExec = data.filter(
      item => item.Step === 'Coordinator review before execution'
    );

    if (coordReviewBeforeExec.length > 0) {
      const incompleteSites = coordReviewBeforeExec
        .filter(
          item =>
            item.ActualStatus === null ||
            item.ActualStatus === '' ||
            Number(item.ActualStatus) === 5
        )
        .map(item => item.SiteID);

      cleanedData = data.filter(
        item =>
          !(item.Step === 'Execution' && incompleteSites.includes(item.SiteID))
      );
    }
  }
  function getStepStatus(step) {
    const steps = cleanedData.filter(item => item.Step === step);
    //this handles the case where the creator step doesnt exist for the other sites but has been completed by the creator
    if (step === 'Creator') {
      if (!steps.length) {
        return 'green';
      }
    }
    //this is needed because they only want one site to have the coordinator review before estimates step which means we need to account for that
    if (step === 'Coordinator review before estimates') {
      const creatorStatus = getStepStatus('Creator');
      const siteCoordinatorStatus = getStepStatus('Site Coordinator Review');
      if (creatorStatus === 'red' || creatorStatus === 'blue') {
        return 'white';
      }
      if (
        siteCoordinatorStatus === 'red' ||
        siteCoordinatorStatus === 'blue' ||
        siteCoordinatorStatus === 'gray' ||
        siteCoordinatorStatus === 'green'
      ) {
        return 'green';
      }
      if (
        steps.some(
          item => item.ActualStatus === '7' || item.ActualStatus === '14'
        )
      ) {
        return 'red';
      }
      if (siteCoordinatorStatus === 'white') {
        return 'blue';
      }
    }

    if (step === 'Site Coordinator Review' || step === 'Owner Review') {
      if (steps.some(item => item.ActualStatus === '11')) {
        return 'gray';
      }
    }

    if (step === 'Coordinator Review') {
      if (steps.some(item => Number(item.ActualStatus) === 15)) {
        return 'blue';
      }
    }

    if (
      steps.some(
        item =>
          Number(item.ActualStatus) === 7 || Number(item.ActualStatus) === 14
      )
    ) {
      return 'red';
    }

    if (
      steps.some(
        item =>
          Number(item.ActualStatus) === 5 ||
          Number(item.ActualStatus) === 1 ||
          Number(item.ActualStatus) === 0 ||
          item.ActualStatus === null ||
          item.ActualStatus === ''
      )
    ) {
      return 'blue';
    }

    if (
      steps.some(item => !['5', '7', '9', '14'].includes(item.ActualStatus))
    ) {
      return 'green';
    }

    return 'white';
  }

  function getReviewDate(step) {
    const steps = data.filter(item => item.Step === step);
    return steps.length > 0 ? steps[steps.length - 1].ReviewDate : null;
  }
  function getSubmittedBy(step) {
    const submittedBySteps = data.filter(item => item.Step === step);
    const submittedBy = submittedBySteps.map(item => item.UserName);
    return submittedBy.join(',') || '';
  }
  return (
    <Stepper
      sx={{
        '& .MuiStepConnector-root': {
          maxWidth: '50px',
          '&:first-of-type': {
            display: 'none',
          },
        },
        '& .MuiStep-root': {
          paddingLeft: '0px',
          paddingRight: '0px',
        },
        '& .MuiStepLabel-iconContainer': {
          paddingRight: '0px',
        },
      }}
    >
      <TrackingFileStep
        date={getReviewDate('Creator')}
        step={t('Creator')}
        submittedBy={getSubmittedBy('Creator')}
        color={getStepStatus('Creator')}
        site={site}
      />
      <TrackingFileStep
        date={getReviewDate('Coordinator review before estimates')}
        step={'Coordinator review before estimates'}
        submittedBy={getSubmittedBy('Coordinator review before estimates')}
        color={getStepStatus('Coordinator review before estimates')}
        site={site}
      />
      <TrackingFileStep
        date={getReviewDate('Site Coordinator Review')}
        step={'Site Coordinator Review'}
        submittedBy={getSubmittedBy('Site Coordinator Review')}
        color={getStepStatus('Site Coordinator Review')}
        site={site}
      />
      <TrackingFileStep
        date={getReviewDate('Owner Review')}
        step={'Owner Review'}
        submittedBy={getSubmittedBy('Owner Review')}
        color={getStepStatus('Owner Review')}
        site={site}
      />
      <TrackingFileStep
        date={getReviewDate('Estimates')}
        step={'Estimates'}
        submittedBy={getSubmittedBy('Estimates')}
        color={getStepStatus('Estimates')}
        site={site}
      />
      <TrackingFileStep
        date={getReviewDate('Coordinator review before execution')}
        step={'Coordinator review before execution'}
        submittedBy={getSubmittedBy('Coordinator review before execution')}
        color={getStepStatus('Coordinator review before execution')}
        site={site}
      />
      {data.find(
        item => item.Step === 'Global Coordinator Review Before Execution'
      ) && (
        <TrackingFileStep
          date={getReviewDate('Global Coordinator Review Before Execution')}
          step={'Global Coordinator Review Before Execution'}
          submittedBy={getSubmittedBy(
            'Global Coordinator Review Before Execution'
          )}
          color={getStepStatus('Global Coordinator Review Before Execution')}
          site={site}
        />
      )}
      <TrackingFileStep
        date={getReviewDate('Execution')}
        step={'Execution'}
        submittedBy={getSubmittedBy('Execution')}
        color={getStepStatus('Execution')}
        site={site}
      />
      <TrackingFileStep
        date={getReviewDate('Coordinator Review')}
        step={'Coordinator Review'}
        submittedBy={getSubmittedBy('Coordinator Review')}
        color={getStepStatus('Coordinator Review')}
        site={site}
      />
    </Stepper>
  );
}

TrackingFileStep.propTypes = {
  step: PropTypes.string.isRequired,
  submittedBy: PropTypes.string,
  date: PropTypes.string,
  color: PropTypes.string,
  site: PropTypes.string.isRequired,
};

function TrackingFileStep({ step, submittedBy, date, color, site }) {
  const location = useLocation();
  const navigate = useNavigate();
  function handleFilterStep(site, step) {
    const params = new URLSearchParams(location.search);
    params.set('site', site);
    params.set('step', step);
    navigate({ search: params.toString() });
  }
  return (
    <Step
      onClick={() => handleFilterStep(site, step)}
      style={{ cursor: 'pointer' }}
      aria-label={step}
      title=" "
    >
      <StepTooltip
        title={step}
        submittedBy={submittedBy}
        date={date}
        color={color}
      />
    </Step>
  );
}

StepTooltip.propTypes = {
  title: PropTypes.string.isRequired,
  submittedBy: PropTypes.string,
  date: PropTypes.string,
  color: PropTypes.string,
};

function StepTooltip({ title, submittedBy, date, color }) {
  let submittedByText = '';
  if (submittedBy) {
    if (color === 'red') {
      submittedByText = `Rejected By ${submittedBy} on ${new Date(date).toLocaleDateString()}`;
    }
    if (color === 'green') {
      submittedByText = `Submitted By ${submittedBy} on ${new Date(date).toLocaleDateString()}`;
    }
    if (color === 'blue') {
      submittedByText = `In Process By ${submittedBy}`;
    }
    if (color === 'white') {
      submittedByText = 'Not yet submitted';
    }
  }
  return (
    <Tooltip
      title={
        <div style={{ padding: '8px' }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {title}
          </Typography>
          <Typography variant="body2">{submittedByText}</Typography>
        </div>
      }
      placement="top-start"
      arrow
    >
      <StepLabel
        StepIconComponent={() => (
          <CircleIcon
            fontSize="small"
            sx={{
              color: color,
              stroke: 'black',
              cursor: color !== '' ? 'pointer' : 'default',
            }}
          />
        )}
      ></StepLabel>
    </Tooltip>
  );
}
export default TrackingFileStepper;
